import type { MenuVersionStatus } from '@/lib/shared-types';
import type { MenuGovernancePerson, MenuVersionRow } from './menuGovernanceApi';

/** `Prisma.Decimal` serialises as a string; formats it for a locale-aware date+time. */
export function formatDateTime(value: string | Date | null | undefined, locale: string): string {
  if (!value) return '—';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

/** "Firstname Lastname", or a dash when the actor is a background job (the cron sweep). */
export function personName(person: MenuGovernancePerson | null | undefined): string {
  if (!person) return '—';
  const name = [person.firstName, person.lastName].filter(Boolean).join(' ').trim();
  return name || '—';
}

/**
 * The statuses `MenuVersionService.loadEditable` still accepts a rule write on.
 * Mirrors `EDITABLE` in `menu-version.service.ts` exactly — the server is the
 * real gate, this is only what decides whether the editor's controls render.
 */
const EDITABLE_STATUSES: MenuVersionStatus[] = ['DRAFT', 'IN_REVIEW'];

export function isEditableStatus(status: MenuVersionStatus): boolean {
  return EDITABLE_STATUSES.includes(status);
}

/**
 * Whether this version has ever been in force — the same test
 * `MenuVersionService.rollback` makes before it will touch a version, mirrored
 * here so the Roll back button is not offered on a draft that never published.
 */
export function hasBeenLive(version: Pick<MenuVersionRow, 'status' | 'effectiveFrom'>): boolean {
  const live: MenuVersionStatus[] = ['SCHEDULED', 'PUBLISHED', 'ROLLED_BACK'];
  return Boolean(version.effectiveFrom) && live.includes(version.status);
}

/**
 * Whether `effectiveFrom` has already passed — the same test
 * `MenuVersionService.cancel` makes to refuse cancelling a version that has
 * already been charging guests.
 */
export function isEffective(
  version: Pick<MenuVersionRow, 'effectiveFrom'>,
  now: number = Date.now(),
): boolean {
  return Boolean(version.effectiveFrom) && new Date(version.effectiveFrom!).getTime() <= now;
}

/** Cancel is offered up to the moment a version takes effect; after that it is a rollback. */
export function isCancellable(version: Pick<MenuVersionRow, 'status' | 'effectiveFrom'>): boolean {
  const cancellable: MenuVersionStatus[] = ['DRAFT', 'IN_REVIEW', 'APPROVED', 'SCHEDULED'];
  return cancellable.includes(version.status) && !isEffective(version);
}

/** A rollback is refused once already rolled back — there is nothing further to revert. */
export function isRollbackable(version: Pick<MenuVersionRow, 'status' | 'effectiveFrom'>): boolean {
  return hasBeenLive(version) && version.status !== 'ROLLED_BACK';
}

/** An ISO instant → the local `yyyy-MM-ddTHH:mm` a `datetime-local` input wants. */
export function toDatetimeLocal(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** The reverse — a `datetime-local` value → an ISO instant, or null when blank/invalid. */
export function fromDatetimeLocal(value: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}
